import React, { useState } from "react";
import { profileUpdate } from "../../services/pofile";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setProjectDetails } from "../../redux/slices/projectSlice";
import { useAuth } from "../../auth/AuthContext";
import { getIpInfo } from "../../services/api";
import { AuthLayout } from "../../components/layout/AuthLayout";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { Alert } from "../../components/ui/Alert";
import { PhoneInput } from "../../components/ui/PhoneInput";

const BUSINESS_FUNCTIONS = [
  "Finance",
  "Legal",
  "HR",
  "Supply Chain",
  "Engineering",
  "Operations",
  "Sales",
  "Information Technology",
  "Other",
];

const selectClassName =
  "h-10 w-full rounded-md border bg-[var(--app-input-bg)] px-3 text-[var(--app-input-text)] border-[var(--app-input-border)] focus:ring-2 focus:ring-[var(--app-primary)] focus:ring-offset-2 focus:outline-none";

function ProfileDetails() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [job, setJob] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login } = useAuth();
  const location = useLocation();
  const { id } = useParams();
  const userId = parseInt(id ?? "", 10);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    const userData = {
      userId,
      userName: name,
      jobTitle: job,
      contact: phone,
      businessType: businessType,
    };

    try {
      const response = await profileUpdate(userData);
      if (response.succeeded) {
        const credentials = location.state as {
          email?: string;
          password?: string;
        };
        if (credentials?.email && credentials?.password) {
          const { ipAddress, userAgent } = await getIpInfo();
          const user = await login(
            credentials.email,
            credentials.password,
            ipAddress,
            userAgent
          );

          const projectId = user.projectDetails?.[0]?.projectId ?? 1;
          localStorage.setItem("projectId", String(projectId));
          dispatch(setProjectDetails(user.projectDetails));
          navigate(`/su/dashboard`);
        }
      }
    } catch (error) {
      console.error("Error during profile update:", error);
      setErrorMessage("Invalid Email Address.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={
        <>
          Complete your <span className="auth-title__accent">profile</span>
        </>
      }
      subtitle="Please provide your details."
    >
      {errorMessage ? (
        <Alert variant="error" description={errorMessage} className="mb-4" />
      ) : null}

      <form className="space-y-4" autoComplete="off" onSubmit={handleSubmit}>
        <Input
          label="Name"
          type="text"
          id="name"
          placeholder="Enter your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <PhoneInput
          label="Contact Number"
          country="pk"
          value={phone}
          onChange={(value) => setPhone(value)}
          placeholder="Enter your phone number"
        />

        <Input
          label="Job Title"
          type="text"
          id="job"
          placeholder="Enter your job title"
          value={job}
          onChange={(e) => setJob(e.target.value)}
          required
        />

        <div className="w-full">
          <label
            htmlFor="businessType"
            className="mb-1.5 block text-sm font-medium text-[var(--app-text)]"
          >
            Business Function<span className="text-red-500 ml-0.5">*</span>
          </label>
          <select
            className={selectClassName}
            id="businessType"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            required
          >
            <option value="" disabled>
              Select business function
            </option>
            {BUSINESS_FUNCTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <Button type="submit" variant="primary" className="w-full" isLoading={loading}>
          {loading ? "Submitting…" : "Submit"}
        </Button>
      </form>

      <div className="mt-6 border-t border-[var(--app-border)] pt-4">
        <p className="text-xs text-[var(--app-text-muted)] m-0 mb-2">
          Your name, company name, and email will not be disclosed to anyone,
          including but not limited to subject matter advisors, administrators,
          and project managers internal to Traxccel, or to any of our partners
          or third parties.
        </p>
        <p className="text-xs text-[var(--app-text-muted)] m-0">
          <Link to="/privacy_policy" className="text-[#0F6B66] hover:underline">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms_condition" className="text-[#0F6B66] hover:underline">
            Terms &amp; Conditions
          </Link>
          .
        </p>
      </div>
    </AuthLayout>
  );
}

export default ProfileDetails;
